(() => {
  'use strict';
  if (window.erpArtworkZoomReady) return;
  window.erpArtworkZoomReady = true;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const prepared = new WeakSet();
  const hosts = new WeakMap();
  let dialog;
  let viewer;
  let canvas;
  let fullImage;
  let title;
  let output;
  let minus;
  let plus;
  let status;
  let opener;
  let zoom = 1;
  let magnifier;
  let magnifierImage;
  let hoverImage;
  let hoverFrame = 0;
  let hoverPoint;
  const triggerPositions = new Map();
  let triggerPositionFrame = 0;

  const sourceFor = image => image.dataset.erpArtworkFull || image.currentSrc || image.src;
  const titleFor = image => image.dataset.erpArtworkTitle || image.closest('[data-erp-artwork-title]')?.dataset.erpArtworkTitle || image.alt || 'Access plan artwork';
  const focus = element => {
    if (!element?.isConnected) return;
    try { element.focus({preventScroll: true}); } catch (error) { element.focus(); }
  };

  function hideMagnifier() {
    hoverImage = null;
    if (hoverFrame) cancelAnimationFrame(hoverFrame);
    hoverFrame = 0;
    if (magnifier) magnifier.hidden = true;
  }

  function createMagnifier() {
    if (magnifier) return;
    magnifier = document.createElement('div');
    magnifier.className = 'erp-artwork-magnifier';
    magnifier.hidden = true;
    magnifier.setAttribute('aria-hidden', 'true');
    magnifierImage = document.createElement('img');
    magnifierImage.alt = '';
    magnifierImage.draggable = false;
    const label = document.createElement('span');
    label.textContent = '2× detail preview';
    magnifier.append(magnifierImage, label);
    document.body.append(magnifier);
  }

  function paintMagnifier() {
    hoverFrame = 0;
    if (!hoverImage || !hoverPoint || !finePointer.matches || dialog?.open) return;
    const rect = hoverImage.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return hideMagnifier();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 12;
    const size = Math.min(320, Math.max(180, viewportWidth * .25), viewportWidth - margin * 2, viewportHeight - margin * 2);
    if (size < 1) return hideMagnifier();
    const x = Math.max(0, Math.min(1, (hoverPoint.x - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (hoverPoint.y - rect.top) / rect.height));
    let left = hoverPoint.x + 28;
    if (left + size > viewportWidth - margin) left = hoverPoint.x - size - 28;
    left = Math.max(margin, Math.min(left, viewportWidth - size - margin));
    const top = Math.max(margin, Math.min(hoverPoint.y - size / 2, viewportHeight - size - margin));
    const imageWidth = rect.width * 2;
    const imageHeight = rect.height * 2;
    const imageLeft = Math.max(Math.min(0, size - imageWidth), Math.min(0, size / 2 - x * imageWidth));
    const imageTop = Math.max(Math.min(0, size - imageHeight), Math.min(0, size / 2 - y * imageHeight));
    magnifier.style.width = `${size}px`;
    magnifier.style.height = `${size}px`;
    magnifier.style.left = `${left}px`;
    magnifier.style.top = `${top}px`;
    magnifierImage.style.width = `${imageWidth}px`;
    magnifierImage.style.height = `${imageHeight}px`;
    magnifierImage.style.left = `${imageLeft}px`;
    magnifierImage.style.top = `${imageTop}px`;
    magnifier.hidden = false;
  }

  function queueMagnifier(image, event) {
    if (!finePointer.matches || event.pointerType === 'touch' || dialog?.open) return;
    createMagnifier();
    if (hoverImage !== image) magnifierImage.src = sourceFor(image);
    hoverImage = image;
    hoverPoint = {x: event.clientX, y: event.clientY};
    if (!hoverFrame) hoverFrame = requestAnimationFrame(paintMagnifier);
  }

  function layoutViewer(center = true) {
    if (!dialog?.open) return;
    const availableWidth = Math.max(1, viewer.clientWidth - 32);
    const availableHeight = Math.max(1, viewer.clientHeight - 32);
    const ratio = fullImage.naturalWidth && fullImage.naturalHeight ? fullImage.naturalWidth / fullImage.naturalHeight : 1;
    const baseWidth = Math.min(availableWidth, availableHeight * ratio);
    const width = Math.max(1, Math.round(baseWidth * zoom));
    const height = Math.max(1, Math.round(width / ratio));
    const oldWidth = canvas.offsetWidth || viewer.clientWidth;
    const oldHeight = canvas.offsetHeight || viewer.clientHeight;
    const previousCenterX = (viewer.scrollLeft + viewer.clientWidth / 2) / Math.max(1, oldWidth);
    const previousCenterY = (viewer.scrollTop + viewer.clientHeight / 2) / Math.max(1, oldHeight);
    fullImage.style.width = `${width}px`;
    fullImage.style.height = `${height}px`;
    const canvasWidth = Math.max(viewer.clientWidth, width + 32);
    const canvasHeight = Math.max(viewer.clientHeight, height + 32);
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;
    viewer.scrollLeft = center ? (canvasWidth - viewer.clientWidth) / 2 : previousCenterX * canvasWidth - viewer.clientWidth / 2;
    viewer.scrollTop = center ? (canvasHeight - viewer.clientHeight) / 2 : previousCenterY * canvasHeight - viewer.clientHeight / 2;
    output.textContent = `${Math.round(zoom * 100)}%`;
    const focusedZoomControl = document.activeElement;
    minus.disabled = zoom <= 1;
    plus.disabled = zoom >= 3;
    if (focusedZoomControl === minus && minus.disabled) focus(plus);
    else if (focusedZoomControl === plus && plus.disabled) focus(minus);
  }

  function changeZoom(next) {
    zoom = Math.max(1, Math.min(3, next));
    layoutViewer(false);
  }

  function createViewer() {
    if (dialog) return;
    dialog = document.createElement('dialog');
    dialog.className = 'erp-artwork-dialog';
    dialog.id = 'erp-artwork-viewer';
    dialog.setAttribute('aria-labelledby', 'erp-artwork-viewer-title');
    dialog.setAttribute('aria-describedby', 'erp-artwork-viewer-help');
    const header = document.createElement('div');
    header.className = 'erp-artwork-dialog__header';
    title = document.createElement('h2');
    title.id = 'erp-artwork-viewer-title';
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'erp-artwork-dialog__close';
    close.textContent = 'Close';
    close.setAttribute('aria-label', 'Close image viewer');
    close.autofocus = true;
    close.addEventListener('click', () => dialog.close());
    header.append(title, close);

    const toolbar = document.createElement('div');
    toolbar.className = 'erp-artwork-dialog__toolbar';
    toolbar.setAttribute('role', 'group');
    toolbar.setAttribute('aria-label', 'Image zoom controls');
    minus = document.createElement('button');
    minus.type = 'button';
    minus.textContent = '−';
    minus.setAttribute('aria-label', 'Zoom out');
    minus.addEventListener('click', () => changeZoom(zoom - .5));
    output = document.createElement('output');
    output.setAttribute('aria-live', 'polite');
    output.setAttribute('aria-label', 'Image zoom');
    plus = document.createElement('button');
    plus.type = 'button';
    plus.textContent = '+';
    plus.setAttribute('aria-label', 'Zoom in');
    plus.addEventListener('click', () => changeZoom(zoom + .5));
    const reset = document.createElement('button');
    reset.type = 'button';
    reset.textContent = 'Fit image';
    reset.addEventListener('click', () => { zoom = 1; layoutViewer(); });
    toolbar.append(minus, output, plus, reset);
    const help = document.createElement('p');
    help.id = 'erp-artwork-viewer-help';
    help.className = 'erp-artwork-dialog__help';
    help.textContent = '100% fits the screen. Zoom in, then scroll or drag with one finger to explore. Browser pinch zoom also works.';
    status = document.createElement('p');
    status.className = 'erp-artwork-dialog__status';
    status.setAttribute('role', 'status');
    status.hidden = true;
    viewer = document.createElement('div');
    viewer.className = 'erp-artwork-dialog__viewport';
    viewer.tabIndex = 0;
    viewer.setAttribute('role', 'region');
    viewer.setAttribute('aria-label', 'Artwork. Use the arrow keys to pan when zoomed in.');
    canvas = document.createElement('div');
    canvas.className = 'erp-artwork-dialog__canvas';
    fullImage = document.createElement('img');
    fullImage.className = 'erp-artwork-dialog__image';
    fullImage.draggable = false;
    fullImage.decoding = 'async';
    fullImage.addEventListener('load', () => { status.hidden = true; layoutViewer(); });
    fullImage.addEventListener('error', () => {
      status.textContent = 'This image could not load. Close the viewer and try again.';
      status.hidden = false;
    });
    canvas.append(fullImage);
    viewer.append(canvas);
    viewer.addEventListener('keydown', event => {
      const pans = {ArrowLeft: [-64, 0], ArrowRight: [64, 0], ArrowUp: [0, -64], ArrowDown: [0, 64]};
      if (event.target === viewer && zoom > 1 && pans[event.key]) {
        event.preventDefault();
        const [left, top] = pans[event.key];
        viewer.scrollBy({left, top, behavior: 'auto'});
      }
    });
    dialog.append(header, toolbar, help, status, viewer);
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('erp-artwork-viewer-open');
      focus(opener);
    });
    document.body.append(dialog);
  }

  function openViewer(image, trigger) {
    hideMagnifier();
    createViewer();
    if (typeof dialog.showModal !== 'function') return;
    opener = trigger;
    title.textContent = titleFor(image);
    fullImage.alt = image.alt || titleFor(image);
    status.textContent = 'Loading full-size artwork…';
    status.hidden = false;
    fullImage.src = sourceFor(image);
    zoom = 1;
    dialog.showModal();
    document.documentElement.classList.add('erp-artwork-viewer-open');
    layoutViewer();
    if (fullImage.complete && fullImage.naturalWidth) status.hidden = true;
    focus(dialog.querySelector('.erp-artwork-dialog__close'));
  }

  function hostFor(image, marked) {
    let anchor = image.closest('a,button');
    if (anchor) {
      let outer = anchor.parentElement?.closest('a,button');
      while (outer) { anchor = outer; outer = anchor.parentElement?.closest('a,button'); }
      let host = hosts.get(anchor);
      if (!host) {
        host = document.createElement('div');
        host.className = 'erp-artwork-zoom-host';
        anchor.before(host);
        host.append(anchor);
        hosts.set(anchor, host);
      }
      return host;
    }
    const host = marked.matches('img') ? image.parentElement : marked;
    host?.classList.add('erp-artwork-zoom-host');
    return host;
  }

  // Read all artwork geometry before writing hit-surface styles.
  function flushTriggerPositions() {
    triggerPositionFrame = 0;
    const positions = [];
    triggerPositions.forEach(({image, host}, trigger) => {
      if (!image.isConnected || !host.isConnected || !trigger.isConnected) return;
      const imageBounds = image.getBoundingClientRect();
      const hostBounds = host.getBoundingClientRect();
      if (imageBounds.width < 1 || imageBounds.height < 1) return;
      positions.push({
        trigger,
        top: `${imageBounds.top - hostBounds.top - host.clientTop}px`,
        left: `${imageBounds.left - hostBounds.left - host.clientLeft}px`,
        width: `${imageBounds.width}px`,
        height: `${imageBounds.height}px`
      });
    });
    triggerPositions.clear();
    positions.forEach(({trigger, ...styles}) => {
      Object.keys(styles).forEach(property => {
        if (trigger.style[property] !== styles[property]) trigger.style[property] = styles[property];
      });
    });
  }

  function queueTriggerPosition(image, host, trigger) {
    triggerPositions.set(trigger, {image, host});
    if (!triggerPositionFrame) triggerPositionFrame = requestAnimationFrame(flushTriggerPositions);
  }

  function prepare(marked) {
    const image = marked.matches('img') ? marked : marked.querySelector('img');
    if (!image || prepared.has(image)) return;
    const host = hostFor(image, marked);
    if (!host) return;
    prepared.add(image);
    // The image itself is the control; its existing product caption remains a link.
    // A sibling native hit surface keeps keyboard access without nesting a button in a link.
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'erp-artwork-trigger';
    trigger.setAttribute('aria-label', `Open full-size image: ${titleFor(image)}`);
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', 'erp-artwork-viewer');
    trigger.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); openViewer(image, trigger); });
    host.append(trigger);
    const positionTrigger = () => queueTriggerPosition(image, host, trigger);
    positionTrigger();
    image.addEventListener('load', positionTrigger);
    if (typeof ResizeObserver === 'function') {
      const observer = new ResizeObserver(positionTrigger);
      observer.observe(host);
      observer.observe(image);
    } else window.addEventListener('resize', positionTrigger, {passive: true});
    trigger.addEventListener('pointerenter', event => queueMagnifier(image, event));
    trigger.addEventListener('pointermove', event => queueMagnifier(image, event));
    trigger.addEventListener('pointerleave', hideMagnifier);
  }

  function initialize(root = document) {
    if (root.matches?.('[data-erp-artwork]')) prepare(root);
    root.querySelectorAll('[data-erp-artwork]').forEach(prepare);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), {once: true});
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  window.addEventListener('resize', () => { hideMagnifier(); layoutViewer(); }, {passive: true});
  window.addEventListener('scroll', hideMagnifier, {passive: true});
  finePointer.addEventListener?.('change', hideMagnifier);
})();
